import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag, ArrowRight } from 'lucide-react';
import { getBlogBySlug, getBlogs } from '../api/blogApi';

// ─── Editor.js Block Renderer ─────────────────────────────────────────────────
// Converts Editor.js JSON blocks to styled React elements. Prevents XSS by not
// using dangerouslySetInnerHTML for raw HTML — only controlled inline styles.
const RenderBlock = ({ block }) => {
  if (!block) return null;
  const { type, data } = block;

  switch (type) {
    case 'header':
      const Tag = `h${data.level || 2}`;
      const sizes = { 1: 'text-4xl', 2: 'text-3xl', 3: 'text-2xl', 4: 'text-xl', 5: 'text-lg', 6: 'text-base' };
      return (
        <div className="max-w-4xl mx-auto">
          <Tag 
            className={`${sizes[data.level] || 'text-2xl'} font-serif font-bold text-gray-900 dark:text-gray-100 mt-10 mb-4 leading-tight`}
            dangerouslySetInnerHTML={{ __html: data.text }}
          />
        </div>
      );

    case 'paragraph':
      return (
        <div className="max-w-4xl mx-auto">
          <p
            className="text-gray-600 dark:text-gray-400 text-lg leading-relaxed mb-6"
            dangerouslySetInnerHTML={{ __html: data.text }}
          />
        </div>
      );

    case 'list':
      const renderListItem = (item, index) => {
        const text = typeof item === 'string' ? item : (item.content || '');
        const nestedItems = typeof item === 'object' && Array.isArray(item.items) && item.items.length > 0 ? item.items : null;
        
        return (
          <li key={index}>
            <span dangerouslySetInnerHTML={{ __html: text }} />
            {nestedItems && (
              <ul className="list-[circle] list-inside ml-6 mt-2 space-y-1">
                {nestedItems.map((nested, nIdx) => renderListItem(nested, nIdx))}
              </ul>
            )}
          </li>
        );
      };

      if (data.style === 'ordered') {
        return (
          <div className="max-w-4xl mx-auto">
            <ol className="list-decimal list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-400 text-lg leading-relaxed pl-4">
              {data.items.map((item, i) => renderListItem(item, i))}
            </ol>
          </div>
        );
      }
      return (
        <div className="max-w-4xl mx-auto">
          <ul className="list-disc list-inside space-y-2 mb-6 text-gray-600 dark:text-gray-400 text-lg leading-relaxed pl-4">
            {data.items.map((item, i) => renderListItem(item, i))}
          </ul>
        </div>
      );

    case 'image':
      return (
        <figure className="my-10">
          <div className="w-full md:w-[80%] mx-auto h-[40vh] md:h-[60vh] lg:h-[80vh] max-h-[700px] rounded-2xl md:rounded-[2rem] overflow-hidden shadow-xl">
            <img
              src={data.file?.url || data.url}
              alt={data.caption || 'Blog image'}
              className="w-full h-full object-cover"
            />
          </div>
          {data.caption && (
            <figcaption className="text-sm text-gray-400 text-center mt-3 italic">
              {data.caption}
            </figcaption>
          )}
        </figure>
      );

    case 'quote':
      return (
        <div className="max-w-4xl mx-auto">
          <blockquote className="my-8 border-l-4 border-amber-400 pl-6 py-2">
            <p className="text-xl font-serif italic text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: data.text }}
            />
            {data.caption && (
              <cite className="text-sm text-gray-400 not-italic mt-2 block">— {data.caption}</cite>
            )}
          </blockquote>
        </div>
      );

    case 'delimiter':
      return <hr className="my-12 border-none border-t-2 border-dashed border-gray-100 dark:border-gray-700" />;

    default:
      // Render unknown blocks as preformatted text (safe fallback)
      return null;
  }
};

const BlogDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setNotFound(false);
        const response = await getBlogBySlug(slug);
        if (response.success && response.data) {
          setBlog(response.data);
          // Fetch related blogs in the background
          fetchRelated(response.data.category, response.data._id);
        } else {
          setNotFound(true);
        }
      } catch (err) {
        console.error('Error fetching blog:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const fetchRelated = async (category, currentId) => {
    try {
      const res = await getBlogs({ status: 'Published', limit: 3 });
      if (res.success) {
        const related = res.data.filter(b => b._id !== currentId).slice(0, 3);
        setRelatedBlogs(related);
      }
    } catch (_) {}
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'MAR 2025';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafaf8] dark:bg-gray-950">
        <div className="animate-pulse max-w-4xl mx-auto px-4 py-16">
          <div className="h-3 w-24 bg-gray-200 rounded-full mb-8"></div>
          <div className="h-12 bg-gray-200 rounded-2xl mb-4 w-4/5"></div>
          <div className="h-12 bg-gray-200 rounded-2xl mb-8 w-3/5"></div>
          <div className="flex gap-4 mb-10">
            <div className="h-3 w-24 bg-gray-200 rounded-full"></div>
            <div className="h-3 w-24 bg-gray-200 rounded-full"></div>
          </div>
          <div className="w-full h-96 bg-gray-200 rounded-[2rem] mb-12"></div>
          {[...Array(4)].map((_, i) => (
            <div key={i} className="mb-4 space-y-2">
              <div className="h-4 bg-gray-100 rounded-full w-full"></div>
              <div className="h-4 bg-gray-100 rounded-full w-5/6"></div>
              <div className="h-4 bg-gray-100 rounded-full w-4/5"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 404 state
  if (notFound) {
    return (
      <div className="min-h-screen bg-[#fafaf8] dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center px-4">
          <div className="text-8xl mb-6">📄</div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">Article Not Found</h1>
          <p className="text-gray-500 dark:text-gray-400 mb-8">The article you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 bg-gray-900 text-white px-8 py-3 rounded-full font-semibold hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft size={18} /> Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fafaf8] dark:bg-gray-950">

      {/* ─── Top Nav Strip ─── */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-700 px-4 py-4">
        <div className="container mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/blogs')}
            className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <ArrowLeft size={18} /> Back to Blog
          </button>
        </div>
      </div>

      {/* ─── Article Layout ─── */}
      <article className="container mx-auto px-4 pt-4 pb-12">
        <div className="max-w-4xl mx-auto">

          {/* 1. Category + Date — same row, just like the reference */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest">
              {blog?.category}
            </span>
            <span className="text-sm text-gray-400 dark:text-gray-500">
              {formatDate(blog?.publishedAt)}
            </span>
          </div>

          {/* 2. Heading — normal case, clean size */}
          <h1 
            className="text-lg md:text-xl font-bold text-gray-900 dark:text-gray-100 leading-snug mb-6"
            dangerouslySetInnerHTML={{ __html: blog?.title || '' }}
          />

        </div>

        {/* 3. Featured Image — Responsive */}
        {blog?.featuredImage && (
          <div className="w-full md:w-[80%] max-w-5xl mx-auto aspect-[16/9] rounded-2xl md:rounded-[2rem] overflow-hidden shadow-xl mb-8">
            <img
              src={blog.featuredImage}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          {/* 4. Author & Date */}
          <div className="flex items-center gap-4 py-4 border-b border-gray-100 dark:border-gray-700 mb-6">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-sm">
              {blog?.author?.[0]?.toUpperCase() || 'B'}
            </div>
            <div>
              <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{blog?.author}</p>
              <span className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                <Calendar size={12} /> {formatDate(blog?.publishedAt)}
              </span>
            </div>
          </div>

          {/* 5. Excerpt */}
          {blog?.excerpt && (
            <div className="mb-10">
              <p 
                className="text-base text-gray-500 dark:text-gray-400 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: blog.excerpt }}
              />
            </div>
          )}
        </div>

        {/* ─── Blog Content (Editor.js Blocks) ─── */}
        <div className="prose-custom w-full">
          {blog?.content?.blocks?.length > 0 ? (
            blog.content.blocks.map((block, index) => (
              <RenderBlock key={block.id || index} block={block} />
            ))
          ) : (
            <p className="text-gray-400 dark:text-gray-500 italic">No content available for this article.</p>
          )}
        </div>
      </article>

      {/* ─── Related Articles ─── */}
      {relatedBlogs.length > 0 && (
        <div className="bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 mt-8 py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-center gap-4 mb-10">
              <p className="text-sm font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">More Articles</p>
              <div className="flex-1 h-[1px] bg-gray-100 dark:bg-gray-700"></div>
              <Link
                to="/blogs"
                className="flex items-center gap-1.5 text-sm font-bold text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              >
                View All <ArrowRight size={14} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedBlogs.map((related) => (
                <Link key={related._id} to={`/blog/${related.slug}`} className="group">
                  <div className="relative h-56 rounded-[1.75rem] overflow-hidden mb-4 shadow-sm group-hover:shadow-xl transition-shadow duration-300">
                    <img
                      src={related.featuredImage}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
                    <div className="absolute bottom-12 left-5">
                      <span className="bg-white/90 text-gray-800 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full">
                        {related.category}
                      </span>
                    </div>
                    <div className="absolute bottom-4 left-5 right-5">
                      <h3 
                        className="text-white font-bold text-sm leading-snug line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: related.title }}
                      />
                    </div>
                  </div>
                  <p 
                    className="text-gray-500 dark:text-gray-400 text-sm line-clamp-2 px-1"
                    dangerouslySetInnerHTML={{ __html: related.excerpt }}
                  />
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetail;
